import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { setUnauthorizedHandler } from '@/shared/api/client';
import { setPageTitle } from '@/shared/branding/apply-branding';
import { useSessionStore } from '@/shared/stores/session';
import { fetchCurrentUser } from '@/features/auth/api';

/**
 * WEB-010 — every view is lazily loaded, so the initial bundle carries only what the
 * first paint needs.
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/features/auth/LoginView.vue'),
    meta: { public: true, layout: 'auth', title: 'Iniciar sesión' },
  },
  {
    path: '/',
    name: 'dashboard',
    component: () => import('@/app/DashboardView.vue'),
    meta: { title: 'Inicio' },
  },
  {
    path: '/buscar',
    name: 'search',
    component: () => import('@/features/search/SearchResultsView.vue'),
    meta: { title: 'Resultados de búsqueda' },
  },
  {
    path: '/ayuda',
    name: 'help',
    component: () => import('@/features/help/HelpView.vue'),
    meta: { title: 'Centro de ayuda' },
  },
  /**
   * A person's own record, without them having to find themselves in a directory of
   * everyone. `/mi-perfil` always means "me", so the address stays stable.
   *
   * `beforeEnter`, not `redirect`: a route-level redirect is evaluated while the target is
   * resolved, which is BEFORE the global guard restores the session from the cookie. It
   * therefore found no employee id on a cold load and sent everybody to the directory.
   */
  {
    path: '/mi-perfil',
    name: 'my-profile',
    component: () => import('@/features/employees/EmployeeDetailView.vue'),
    beforeEnter: () => {
      const employeeId = useSessionStore().user?.employeeId ?? null;

      // An account with no directory record — a system account — has no profile to show.
      // The directory is the nearest honest destination.
      return employeeId === null
        ? { name: 'employees' }
        : { name: 'employee-detail', params: { id: employeeId } };
    },
    meta: { title: 'Mi perfil' },
  },
  {
    path: '/empleados',
    name: 'employees',
    component: () => import('@/features/employees/EmployeeListView.vue'),
    meta: { title: 'Directorio' },
  },
  {
    // Una página entera y no un modal: la decisión fila por fila sobre las coincidencias
    // no entra en un cuadro de diálogo sin volverse ilegible.
    path: '/empleados/importar',
    name: 'employee-import',
    component: () => import('@/features/employees/EmployeeImportView.vue'),
    meta: { title: 'Importar empleados', permission: 'employee.write' },
  },
  {
    path: '/empleados/nuevo',
    name: 'employee-create',
    component: () => import('@/features/employees/EmployeeFormView.vue'),
    meta: { title: 'Nueva persona', permission: 'employee.write' },
  },
  {
    path: '/empleados/:id',
    name: 'employee-detail',
    component: () => import('@/features/employees/EmployeeDetailView.vue'),
    meta: { title: 'Perfil' },
  },
  {
    path: '/empleados/:id/editar',
    name: 'employee-edit',
    component: () => import('@/features/employees/EmployeeFormView.vue'),
    meta: { title: 'Editar persona', permission: 'employee.write' },
  },
  {
    path: '/organigrama/:id?',
    name: 'org-chart',
    component: () => import('@/features/employees/OrgChartView.vue'),
    meta: { title: 'Organigrama' },
  },
  {
    path: '/documentos',
    name: 'documents',
    component: () => import('@/features/documents/DocumentListView.vue'),
    meta: { title: 'Documentos', permission: 'document.read' },
  },
  {
    path: '/documentos/nuevo',
    name: 'document-create',
    component: () => import('@/features/documents/DocumentFormView.vue'),
    meta: { title: 'Nuevo documento', permission: 'document.write' },
  },
  {
    path: '/documentos/:id',
    name: 'document-detail',
    component: () => import('@/features/documents/DocumentDetailView.vue'),
    meta: { title: 'Documento', permission: 'document.read' },
  },
  {
    path: '/documentos/:id/editar',
    name: 'document-edit',
    component: () => import('@/features/documents/DocumentFormView.vue'),
    meta: { title: 'Editar documento', permission: 'document.write' },
  },
  {
    path: '/documentos/:id/cumplimiento',
    name: 'document-compliance',
    component: () => import('@/features/documents/ComplianceView.vue'),
    meta: { title: 'Cumplimiento', permission: 'compliance.read' },
  },
  {
    path: '/noticias',
    name: 'news',
    component: () => import('@/features/news/NewsListView.vue'),
    meta: { title: 'Comunicados', permission: 'news.read' },
  },
  {
    path: '/noticias/nueva',
    name: 'news-create',
    component: () => import('@/features/news/NewsFormView.vue'),
    meta: { title: 'Nuevo comunicado', permission: 'news.write' },
  },
  {
    path: '/noticias/:id',
    name: 'news-detail',
    component: () => import('@/features/news/NewsDetailView.vue'),
    meta: { title: 'Comunicado', permission: 'news.read' },
  },
  {
    path: '/noticias/:id/editar',
    name: 'news-edit',
    component: () => import('@/features/news/NewsFormView.vue'),
    meta: { title: 'Editar comunicado', permission: 'news.write' },
  },
  {
    path: '/beneficios',
    name: 'benefits',
    component: () => import('@/features/benefits/BenefitsView.vue'),
    meta: { title: 'Beneficios', permission: 'benefit.read' },
  },
  {
    path: '/beneficios/convenios/nuevo',
    name: 'partner-offer-create',
    component: () => import('@/features/benefits/PartnerOfferFormView.vue'),
    meta: { title: 'Nuevo convenio', permission: 'benefit.write' },
  },
  {
    path: '/beneficios/convenios/:id',
    name: 'partner-offer-detail',
    component: () => import('@/features/benefits/PartnerOfferDetailView.vue'),
    meta: { title: 'Convenio', permission: 'benefit.read' },
  },
  {
    path: '/beneficios/convenios/:id/editar',
    name: 'partner-offer-edit',
    component: () => import('@/features/benefits/PartnerOfferFormView.vue'),
    meta: { title: 'Editar convenio', permission: 'benefit.write' },
  },
  {
    path: '/beneficios/internos/nuevo',
    name: 'internal-benefit-create',
    component: () => import('@/features/benefits/InternalBenefitFormView.vue'),
    meta: { title: 'Nuevo beneficio', permission: 'benefit.write' },
  },
  {
    path: '/beneficios/internos/:id',
    name: 'internal-benefit-detail',
    component: () => import('@/features/benefits/InternalBenefitDetailView.vue'),
    meta: { title: 'Beneficio', permission: 'benefit.read' },
  },
  {
    path: '/beneficios/internos/:id/editar',
    name: 'internal-benefit-edit',
    component: () => import('@/features/benefits/InternalBenefitFormView.vue'),
    meta: { title: 'Editar beneficio', permission: 'benefit.write' },
  },
  {
    path: '/ausencias',
    name: 'leave',
    component: () => import('@/features/leave/LeaveView.vue'),
    meta: { title: 'Ausencias', permission: 'leave.read' },
  },
  {
    path: '/ausencias/nueva',
    name: 'leave-create',
    component: () => import('@/features/leave/LeaveRequestFormView.vue'),
    meta: { title: 'Solicitar ausencia', permission: 'leave.write' },
  },
  {
    path: '/ausencias/pendientes',
    name: 'leave-approvals',
    component: () => import('@/features/leave/LeaveApprovalsView.vue'),
    meta: { title: 'Solicitudes por resolver', permission: 'leave.approve' },
  },
  {
    path: '/ausencias/calendario',
    name: 'leave-calendar',
    component: () => import('@/features/leave/LeaveCalendarView.vue'),
    meta: { title: 'Calendario del equipo', permission: 'leave.read' },
  },
  {
    path: '/planillas',
    name: 'payroll-periods',
    component: () => import('@/features/payroll/PayrollPeriodsView.vue'),
    meta: { title: 'Planillas de pago', permission: 'payroll.read' },
  },
  {
    path: '/liquidaciones',
    name: 'severance',
    component: () => import('@/features/payroll/SeveranceView.vue'),
    meta: { title: 'Liquidaciones finales', permission: 'severance.read' },
  },
  {
    path: '/planillas/aguinaldo',
    name: 'aguinaldo',
    component: () => import('@/features/payroll/AguinaldoView.vue'),
    meta: { title: 'Aguinaldo', permission: 'payroll.read' },
  },
  {
    path: '/planillas/parametros',
    name: 'payroll-parameters',
    component: () => import('@/features/payroll/PayrollParametersView.vue'),
    // Declared before `:period`; find-my-way prefers the static segment either way, so a
    // period is never parsed out of this path.
    meta: { title: 'Parámetros de liquidación', permission: 'payroll.read' },
  },
  {
    path: '/planillas/:period',
    name: 'payroll-period',
    component: () => import('@/features/payroll/PayrollPeriodView.vue'),
    meta: { title: 'Planilla', permission: 'payroll.read' },
  },
  {
    path: '/liquidacion',
    name: 'payroll',
    component: () => import('@/features/payroll/PayrollView.vue'),
    // `compensation.read` is what Human Resources holds and a manager does not: a manager
    // may see their own team's salaries, not every account number in the organisation.
    meta: { title: 'Liquidación de haberes', permission: 'compensation.read' },
  },
  {
    path: '/marca',
    name: 'branding',
    component: () => import('@/features/branding/BrandingView.vue'),
    meta: { title: 'Marca del sitio', permission: 'branding.manage' },
  },
  {
    path: '/ausencias/vacaciones',
    name: 'leave-overview',
    component: () => import('@/features/leave/LeaveOverviewView.vue'),
    meta: { title: 'Vacaciones por funcionario', permission: 'leave.manage' },
  },
  {
    path: '/ausencias/calendarios',
    name: 'work-calendars',
    component: () => import('@/features/leave/WorkCalendarsView.vue'),
    meta: { title: 'Calendarios laborales', permission: 'leave.read' },
  },
  {
    path: '/ausencias/parametros',
    name: 'leave-settings',
    component: () => import('@/features/leave/LeaveSettingsView.vue'),
    meta: { title: 'Parámetros de ausencias', permission: 'leave.read' },
  },
  {
    path: '/ausencias/:id',
    name: 'leave-detail',
    component: () => import('@/features/leave/LeaveRequestDetailView.vue'),
    meta: { title: 'Solicitud de ausencia', permission: 'leave.read' },
  },
  {
    path: '/capacitacion',
    name: 'training',
    component: () => import('@/features/training/TrainingCatalogView.vue'),
    meta: { title: 'Capacitación', permission: 'training.read' },
  },
  {
    path: '/capacitacion/mis-cursos',
    name: 'my-training',
    component: () => import('@/features/training/MyTrainingView.vue'),
    meta: { title: 'Mis cursos', permission: 'training.read' },
  },
  {
    path: '/capacitacion/nuevo',
    name: 'course-create',
    component: () => import('@/features/training/CourseFormView.vue'),
    meta: { title: 'Nuevo curso', permission: 'training.write' },
  },
  {
    path: '/capacitacion/:id',
    name: 'course-detail',
    component: () => import('@/features/training/CourseDetailView.vue'),
    meta: { title: 'Curso', permission: 'training.read' },
  },
  {
    path: '/capacitacion/:id/editar',
    name: 'course-edit',
    component: () => import('@/features/training/CourseFormView.vue'),
    meta: { title: 'Editar curso', permission: 'training.write' },
  },
  {
    path: '/capacitacion/:id/leccion/:lessonId',
    name: 'course-lesson',
    component: () => import('@/features/training/LessonView.vue'),
    meta: { title: 'Lección', permission: 'training.read' },
  },
  {
    path: '/capacitacion/:id/evaluacion',
    name: 'course-quiz',
    component: () => import('@/features/training/QuizView.vue'),
    meta: { title: 'Evaluación', permission: 'training.read' },
  },
  {
    path: '/capacitacion/:id/cumplimiento',
    name: 'course-completions',
    component: () => import('@/features/training/CourseCompletionsView.vue'),
    meta: { title: 'Cumplimiento', permission: 'compliance.read' },
  },
  {
    path: '/certificados/:id',
    name: 'certificate',
    component: () => import('@/features/training/CertificateView.vue'),
    meta: { title: 'Certificado', permission: 'training.read' },
  },
  {
    /*
     * Public, and outside the application shell: this is the one screen somebody who does
     * not work here is meant to reach. A certificate nobody outside can check is a picture.
     */
    path: '/verificar/:code?',
    name: 'certificate-verify',
    component: () => import('@/features/training/CertificateVerifyView.vue'),
    meta: { public: true, layout: 'bare', title: 'Verificar certificado' },
  },
  {
    path: '/sin-permiso',
    name: 'forbidden',
    component: () => import('@/app/ForbiddenView.vue'),
    meta: { title: 'Sin permiso' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/app/NotFoundView.vue'),
    meta: { public: true, title: 'Página no encontrada' },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  // PQ-005 — restoring the scroll position on back is part of navigation behaving the
  // way the platform taught users to expect.
  scrollBehavior: (_to, _from, savedPosition) => savedPosition ?? { top: 0 },
});

router.beforeEach(async (to) => {
  const session = useSessionStore();

  // The session lives in an HttpOnly cookie the page cannot read, so on a cold load the
  // only way to know who is signed in is to ask the server.
  if (!session.resolved) {
    try {
      session.set(await fetchCurrentUser());
    } catch {
      session.clear();
    }
  }

  if (to.meta.public === true) {
    return true;
  }

  if (!session.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  const required = to.meta.permission;
  if (typeof required === 'string' && !session.can(required)) {
    // The route is hidden in the navigation too, but hiding is not enforcement: the
    // server rejects the call regardless (FRONT-006). This only avoids showing a screen
    // that would fail.
    return { name: 'forbidden' };
  }

  return true;
});

router.afterEach((to) => {
  // PQ-005 — the document title reflects where the user is, which also gives screen
  // reader users the announcement that a client-side navigation happened.
  // The site name comes from branding, so renaming the site renames the browser tab too.
  setPageTitle(typeof to.meta.title === 'string' ? to.meta.title : '');
});

/**
 * FRONT-004 — session expiry is handled once, centrally. The requirement is explicit: an
 * API call after the session lapses returns the user to the login screen, and the route
 * they wanted is preserved so they land back where they were.
 */
setUnauthorizedHandler(() => {
  const session = useSessionStore();
  session.clear();

  const current = router.currentRoute.value;
  if (current.name !== 'login') {
    void router.replace({ name: 'login', query: { redirect: current.fullPath } });
  }
});
